import { useForm } from 'react-hook-form';
import { resolveApiError, useTranslation } from '@eon/i18n';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  TextField,
  toast,
} from '@eon/ui';
import { hooks } from '@eon/api-client';
import { isValidTurkishMobile, normalizeTurkishPhone } from '@eon/utils';

interface InviteUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface FormValues {
  name: string;
  phoneNumber: string;
  message: string;
}

// Invitations are SMS-only for now: the e-mail provider is unavailable, so
// the e-mail field is hidden until delivery is restored (legacy CSMS also
// delivered end-user invitations via SMS).
export function InviteUserDialog({ open, onOpenChange }: InviteUserDialogProps) {
  const { t } = useTranslation();
  const invite = hooks.useOmInviteUser();
  const { register, handleSubmit, reset, setValue, formState } = useForm<FormValues>({
    defaultValues: { name: '', phoneNumber: '', message: '' },
  });

  const onPhoneBlur = (value: string) => {
    const normalized = normalizeTurkishPhone(value);
    if (normalized !== value) setValue('phoneNumber', normalized);
  };

  const onSubmit = handleSubmit(async (values) => {
    const phone = normalizeTurkishPhone(values.phoneNumber);
    if (!phone) {
      toast.error(t('portal.users.invitePhoneRequired'));
      return;
    }
    if (!isValidTurkishMobile(phone)) {
      toast.error(t('portal.users.invitePhoneInvalid'));
      return;
    }
    try {
      await invite.mutateAsync({
        name: values.name.trim() || undefined,
        phoneNumber: phone,
        message: values.message.trim() || undefined,
      });
      toast.success(t('portal.users.inviteSent'));
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(resolveApiError(err, t));
    }
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('portal.users.inviteTitle')}</DialogTitle>
          <DialogDescription>{t('portal.users.inviteDescriptionSms')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="invite-name">{t('user.name')}</Label>
            <Input id="invite-name" {...register('name')} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="invite-phone">{t('auth.phone')}</Label>
            <Input
              id="invite-phone"
              type="tel"
              inputMode="tel"
              placeholder="+905xxxxxxxxx"
              {...register('phoneNumber', {
                onBlur: (e) => onPhoneBlur(e.target.value),
              })}
            />
            <p className="text-muted-foreground text-xs">{t('portal.users.inviteEmailDisabled')}</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="invite-message">{t('portal.users.inviteMessage')}</Label>
            <TextField id="invite-message" {...register('message')} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('actions.cancel')}
            </Button>
            <Button type="submit" disabled={formState.isSubmitting || invite.isPending}>
              {t('group.invite.send')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
